<template>
  <n-dropdown
    trigger="click"
    :options="options"
    placement="bottom-end"
    @select="onSelect"
  >
    <div class="user-trigger">
      <span v-if="isLoggedIn" class="avatar-ring auth">{{ initial }}</span>
      <span v-else class="avatar-ring guest">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle
            cx="12"
            cy="8"
            r="4"
            stroke="currentColor"
            stroke-width="1.8"
          />
          <path
            d="M4 20c1.8-4 5-6 8-6s6.2 2 8 6"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </span>

      <span v-if="isLoggedIn" class="user-trigger-label">{{ username }}</span>

      <svg
        class="chevron"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M6 9l6 6 6-6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </n-dropdown>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
  isLoggedIn: { type: Boolean, default: false },
  username: { type: String, default: "" },
});

// El componente no decide qué hacer al seleccionar una opción,
// solo emite la intención. La lógica de sesión/routing vive en quien lo use.
const emit = defineEmits([
  "login",
  "register",
  "profile",
  "my-quotes",
  "logout",
]);

const initial = computed(() => props.username.trim().charAt(0).toUpperCase());

// "Mi perfil" solo va en el desplegable en resoluciones de móvil (mismo
// umbral que ya usa el resto del header, p. ej. SearchBar.vue); en el
// resto de tamaños se asume que se llegará a esa vista de otra forma.
const MOBILE_QUERY = "(max-width: 1150px)";
const mobileQuery = window.matchMedia(MOBILE_QUERY);
const isMobile = ref(mobileQuery.matches);

function handleMobileChange(event) {
  isMobile.value = event.matches;
}

onMounted(() => mobileQuery.addEventListener("change", handleMobileChange));
onBeforeUnmount(() =>
  mobileQuery.removeEventListener("change", handleMobileChange)
);

const options = computed(() => {
  if (!props.isLoggedIn) {
    return [
      { label: "Iniciar sesión", key: "login" },
      { label: "Crear cuenta", key: "register" },
    ];
  }

  const items = [];
  if (isMobile.value) {
    items.push({ label: "Mis frases", key: "my-quotes" });
  }
  items.push(
    { label: "Mi perfil", key: "profile" },
    { type: "divider", key: "d1" },
    { label: "Cerrar sesión", key: "logout" }
  );
  return items;
});

function onSelect(key) {
  emit(key);
}
</script>

<style scoped>
.user-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 5px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #fff8ec;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease,
    transform 0.1s ease;
}
.user-trigger:hover {
  border-color: var(--melon);
  background: #fffaf1;
}
.user-trigger:active {
  transform: scale(0.98);
}

.avatar-ring {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.avatar-ring.guest {
  background: transparent;
  border: 1.5px solid var(--ink-soft);
  color: var(--ink-soft);
}
.avatar-ring.auth {
  background: var(--melon);
  color: #fff8ec;
  font-family: "Fraunces", serif;
  font-weight: 600;
  font-size: 14px;
}

.user-trigger-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--ink);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  color: var(--ink-soft);
  flex-shrink: 0;
}

@media (max-width: 520px) {
  .user-trigger-label {
    display: none;
  }
  .user-trigger {
    padding: 5px;
  }
}
</style>